import { type QueryFunctionContext, type UseQueryResult } from "@tanstack/react-query";
import { type ColumnDataPoint } from "components/charts/types/ColumnChart";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { type AxisDomain, format as d3Formatter, scaleLinear, type scaleLog } from "d3";
import { AccountTypes, type AddOn } from "features/Accounts/types";
import { type ChartPeriod, CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import { isEqual } from "lodash";
import moment from "moment";
import sanitize from "sanitize-filename";
import { NoData, NoDataMonth, NoFilteredData } from "../../../../components/charts";
import { type getFormatFromValueTypes } from "../../../../components/charts/shared/public";
import { RStatus } from "../../../Application/globaltypes/fetchRequest";
import { type BasePerformanceRequestFilterParams } from "../models";
import { SERVER_DATE_ONLY_FORMAT } from "utils/dateTimeUtils";
import { AddOnIds, AlertTypes, PriorityLevels, SortingDirection } from "enums";
import environmentConfig from "configuration/environmentConfig";
import { type AxiosResponse } from "axios";
import { sendTransientNotification } from "features/Notifications/state/notificationsActions";
import { setExporting } from "features/Reporting/state/export/exportSlice";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import accountsDataService from "../../../Accounts/services/accountsDataService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquareArrowDown,
  faSquareArrowUp,
  faSquareExclamation,
  faCircleCheck,
  faCircleMinus,
  faClockNine,
} from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-regular-svg-icons";
import { reportingColors } from "./constants";
import type { DropdownItemProps } from "semantic-ui-react";
import { type AccountType } from "../../../../interfaces/Account";
import styles from "./performanceUtils.module.scss";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { roundToTwoDigits } from "./roundToTwoDigits";
import { type ReactNode } from "react";

export type selectedType = "outreach" | "interaction" | undefined;

export type LineChartState = {
  yScale: typeof scaleLinear | typeof scaleLog;
  yFormatterFunc: string | undefined;
  yExtraTickFormat?: ReturnType<typeof getFormatFromValueTypes>;
};

export type Accounts = { value: string | number; text: string };
export type PacksListItem = { value: number; text: string };

export interface PerformanceFilter {
  dateFrom: string;
  dateTo: string;
}

export interface PerformanceWithAccountFilter {
  dateFrom: string;
  dateTo: string;
  showCustomers?: boolean;
  accounts?: Accounts[] | number[];
  packId?: PacksListItem[];
  includeMyData?: boolean;
  includeVideoType?: boolean;
  type?: AccountType;
  isDistinct?: boolean;
  aggregation?: string;
  versionNumber?: number | null;
  videoType?: "All" | "Video" | "EmbeddedVideo";
  priority?: number[];
  status?: number[];
  packStatusId?: number;
  packTypeId?: number;
  addOnIds?: number[];
  flowId?: number;
}

const emptyFilter = { dateFrom: "", dateTo: "" };

export const columnDateFormatter = (d: AxisDomain): string => {
  const asDate = new Date(d.toString());
  return `${asDate.getMonth() + 1}/${asDate.getDate()}`;
};

export const yAxisNumberFormatter = (d: d3.AxisDomain) => {
  if (typeof d === "number" || !isNaN(Number(d))) {
    return d3Formatter(".2~s")(+d);
  }

  return d.toLocaleString();
};

export const linearChartState: LineChartState = {
  yScale: scaleLinear,
  yFormatterFunc: ",d",
};

export const longFormDateFilter = (dateFrom: string, dateTo: string, ...other: any) => ({
  dateFrom: moment(dateFrom).format("MM/DD/YYYY"),
  dateTo: moment(dateTo).format("MM/DD/YYYY"),
  ...other,
});

export const dateRange30 = (): PerformanceFilter => {
  const today = moment().toISOString();
  const todayMinus30 = moment().subtract(30, "days").toISOString();
  let formattedDates = longFormDateFilter(today, todayMinus30);
  return {
    dateTo: formattedDates.dateFrom,
    dateFrom: formattedDates.dateTo,
  };
};

export function sendExportRequest(
  request: (abortController: AbortController) => Promise<AxiosResponse<unknown>>,
  fileName: string,
  abortController: AbortController,
  customSuccessNotification?: { title: string; message: ReactNode; type: AlertTypes },
) {
  abortController.abort();
  abortController = new AbortController();
  return async (dispatch: AppDispatch) => {
    dispatch(setExporting(true));
    try {
      const response = await request(abortController);
      dispatch(
        sendTransientNotification(
          customSuccessNotification || {
            title: "Export Complete!",
            message: "File download has begun.",
            type: AlertTypes.success,
          },
          5,
        ),
      );

      downloadExcelExport(response.data, fileName);
    } catch (err) {
      // Request was aborted manually, no need to change anything
      if (err.code === "ERR_CANCELED") {
        return;
      }
      dispatch(
        sendTransientNotification(
          {
            title: "Export failed",
            message: "Please try again later. If this persists, please contact BrainStorm support.",
            type: AlertTypes.error,
          },
          5,
        ),
      );
    } finally {
      dispatch(setExporting(false));
    }
  };
}

const validDateFormats = [
  "M/D/YY",
  "MM/D/YY",
  "M/DD/YY",
  "MM/DD/YY",
  "M/D/YYYY",
  "MM/D/YYYY",
  "M/DD/YYYY",
  "MM/DD/YYYY",
];
export const validDateWithMoment = (dateString: string) => moment(dateString, validDateFormats, true).isValid();

export const noData = (
  dateFilter: PerformanceFilter | BasePerformanceRequestFilterParams | null,
  isDateAllTime?: boolean,
) => {
  if (isDateAllTime) {
    return <NoData />;
  }
  return dateFilter === null || dateFilter.dateFrom || dateFilter.dateTo ? <NoFilteredData /> : <NoDataMonth />;
};

export const normalizeDateString = (date: string) => {
  const parsed = new Date(date);
  return new Date(new Date(parsed.getTime() - parsed.getTimezoneOffset() * -60000));
};

export const filterCallback = (filter: PerformanceFilter, emailFilter: PerformanceFilter) => {
  if (emailFilter.dateFrom !== filter.dateFrom || emailFilter.dateTo !== filter.dateTo) {
    return { dateFrom: filter.dateFrom, dateTo: filter.dateTo };
  }
  return emptyFilter;
};

export const isLoading = (status: RStatus) => {
  return status === RStatus.Pending;
};

export const invalidFilterData = (funnelData: { [key: string]: number | undefined }, filter: PerformanceFilter) => {
  const zeroValues = Object.values(funnelData).every((value) => value === 0 || value === undefined);
  const filterValues = Object.values(filter).every((value) => value !== "");

  return zeroValues && filterValues;
};

export const isFilterPresent = (dateFilter: PerformanceFilter) => {
  return !isEqual(dateFilter, emptyFilter);
};

export const dataPresent = (data?: { [key: string]: number }) => {
  if (data === undefined) return false;

  const keysExist = Object.keys(data).length !== 0;
  const someValueHasData = Object.values(data).some((item) => item !== 0);

  return keysExist && someValueHasData;
};

export const validLineData = (lines: number[][]): boolean => {
  return (
    !!lines.length &&
    lines.every((p) => p !== undefined) &&
    lines.some((line) => line.some((num) => num !== null)) &&
    lines.some((line) => line.some((num) => num !== 0))
  );
};

export const noBarData = (...data: number[]) => data.every((num) => num <= 0 || num === undefined);
export const validBarData = (data: DataPoint[] | ColumnDataPoint[]) => {
  return data.length > 0 && data.some((d) => d.value > 0);
};

export const canUseChildAccount = (childAccountId?: number, hasCreateAccountsAddOn?: boolean) => {
  if (typeof childAccountId !== "number") {
    return false;
  }

  if (hasCreateAccountsAddOn === undefined) {
    return true;
  }

  return hasCreateAccountsAddOn;
};

export const isUserFromCurrentAccount = (
  userAccountId: number | null | undefined,
  accountId?: number,
  childAccountId?: number,
  hasCreateAccountsAddOn?: boolean,
) => {
  if (userAccountId === null || userAccountId === undefined) {
    return false;
  }

  const childAccountEnabled = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
  const resolvedAccountId = childAccountEnabled ? childAccountId : accountId;

  if (resolvedAccountId === null || resolvedAccountId === undefined) {
    return false;
  }

  return userAccountId === resolvedAccountId;
};

type LinkNavigationParams = {
  childAccountEnabled: boolean;
  isUserFromCurrentAccount: boolean;
  childAccountId?: number;
  fallbackAccountId?: number | null;
};

export const getLinkNavigationProps = ({
  childAccountEnabled,
  isUserFromCurrentAccount,
  childAccountId,
  fallbackAccountId,
}: LinkNavigationParams): { accountId?: number; target?: string } => {
  let accountId: number | undefined;

  if (childAccountEnabled && typeof childAccountId === "number") {
    accountId = childAccountId;
  } else if (!isUserFromCurrentAccount && fallbackAccountId != null) {
    accountId = fallbackAccountId;
  }

  const target = childAccountEnabled || !isUserFromCurrentAccount ? "_blank" : undefined;

  return { accountId, target };
};

export const noTableData = <T,>(rows: Array<T>): boolean => rows.length === 0;

export function formattedAverageTime(num: number) {
  if (num || num === 0) {
    const millisecondsPerSecond = 1000;
    const secondsPerMinute = 60;
    const secondsPerHour = 3600;
    const secondsPerDay = 86400;
    const minutesPerHour = 60;
    const hoursPerDay = 24;

    const fromTime = new Date(+0);
    const toTime = new Date(num * millisecondsPerSecond);

    // get total seconds between the times
    let delta = Math.abs(toTime.getTime() - fromTime.getTime()) / millisecondsPerSecond;

    // calculate (and subtract) whole days
    const days = Math.floor(delta / secondsPerDay);
    delta -= days * secondsPerDay;

    // calculate (and subtract) whole hours
    const hours = Math.floor(delta / secondsPerHour) % hoursPerDay;
    delta -= hours * secondsPerHour;

    // calculate (and subtract) whole minutes
    const minutes = Math.floor(delta / minutesPerHour) % minutesPerHour;
    delta -= minutes * minutesPerHour;

    const seconds = Math.floor(delta % secondsPerMinute);

    if (days !== 0) {
      return `${days.toLocaleString()}d ${hours}h`;
    } else if (hours !== 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes !== 0) {
      return `${minutes}m ${seconds}s`;
    } else if (seconds !== 0) {
      return `${seconds}s`;
    }
    return "0s";
  }
  return "";
}

export function downloadExcelExport(data: any, fileName: string) {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", sanitize(`${fileName}.xlsx`));
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const createDateRange = (dateFrom?: string, dateTo?: string, dateFormat = "MM/DD/YYYY") => {
  const start = moment(dateFrom, dateFormat).local(true);
  const end = moment(dateTo, dateFormat).local(true);
  const dates = [];
  let current = start.clone();

  while (current.isSameOrBefore(end)) {
    dates.push(current.toDate());
    current.add(1, "day");
  }

  return dates;
};

export const getRange = <T,>(
  dateRange: Date[],
  boilerplateObject: T,
  dateFormat = "YYYY-MM-DDTHH:mm:ssZ",
): (T & { Date: string })[] => {
  const dates = [];
  for (const date of dateRange) {
    const boiler = {
      ...boilerplateObject,
      Date: moment(date).local(true).format(dateFormat),
    };
    dates.push(boiler);
  }
  return dates;
};

export const findIndexInRange = (date: string | null | undefined, state: { Date: string }[]) => {
  if (!date) return -1;

  const formattedDate = moment(date)
    .startOf("day")
    .set({ hour: 0, minute: 0, second: 0 })
    .format("YYYY-MM-DDTHH:mm:ssZ");

  return state.findIndex(
    (item) => moment(item.Date).format("YYYY-MM-DDTHH:mm:ssZ") === moment(formattedDate).format("YYYY-MM-DDTHH:mm:ssZ"),
  );
};

export const transitionTime = 500;
export const barPadding = 0.2;

export const lineChartMargins = { top: 34, left: 75, right: 55, bottom: 39 };
export const groupedBarChartMargins = lineChartMargins;
export const horizontalBarChartMargins = { top: 12, right: 50, bottom: 40, left: 120 };
export const columnChartMargins = { top: 20, left: 80, right: 20, bottom: 30 };
export const sankeyWrapperMargins = { top: 50, left: 50, right: 50, bottom: 50 };

export const totalActivity = "Total Activity";
export const dailyActivity = "Daily Activity";
export const lineChartTitles = [totalActivity, dailyActivity];
export const packTitles = ["Trials", "Purchased"];
export const starts = "Starts";
export const inProgress = "In Progress";
export const completes = "Completes";
export const completions = "Completions";
export const chartLegendLabels = [starts, completes];

export const chartColorScale = [reportingColors.blue, reportingColors.yellow];
export const emailReportColorScale = [reportingColors.blue, reportingColors.greenDark, reportingColors.yellow];

type LineChartKeys<T> = "Date" | keyof T;
type IncomingLineChartValues<Key extends string | number | symbol> = Key extends "Date" ? string : number;
type OutgoingLineChartValues<Key extends string | number | symbol> = Key extends "Date" ? Date : number;

type IncomingData<T> = {
  [key in LineChartKeys<T>]: IncomingLineChartValues<key>;
};

export type FormattedLineData<T extends IncomingData<T>> = {
  [key in LineChartKeys<T>]: OutgoingLineChartValues<key>[];
};
type OtherFormattedDataOptional<T extends IncomingData<T>> = Partial<FormattedLineData<T>>;

export const lineChartFactory = <T extends IncomingData<T>>(dataPoints: T[]) => {
  if (dataPoints.length === 0) return {} as FormattedLineData<T>;
  const getKeys = Object.keys(dataPoints[0]) as (keyof T)[];
  const formattedData: OtherFormattedDataOptional<T> = {};

  for (const key of getKeys) {
    formattedData[key] = [];
  }

  for (const dataPoint of dataPoints) {
    for (const [key, value] of Object.entries(dataPoint)) {
      if (key === "Date") {
        (formattedData as FormattedLineData<T>).Date.push(moment(value).local(true).toDate());
      } else {
        (formattedData as FormattedLineData<T>)[key as keyof T].push(value);
      }
    }
  }

  return formattedData as FormattedLineData<T>;
};

export const canViewCustomersInformation = (accountType: AccountTypes, addOns?: AddOn[]) => {
  const customerTypeValid = accountType !== AccountTypes.Customer;
  const addOnsPresent = Array.isArray(addOns);
  if (!addOnsPresent) {
    return customerTypeValid;
  }
  const foundAddon = addOns.find((addon) => addon.id === AddOnIds.CreateAccounts);
  const marketPlaceAddon = addOns.find((addon) => addon.id === AddOnIds.Marketplace);
  const addOnValid = !!foundAddon?.isEnabled;
  const marketPlaceValid = !!marketPlaceAddon?.isEnabled;
  return customerTypeValid || addOnValid || marketPlaceValid;
};

export type defaultDateFilterType = {
  dateFrom: string;
  dateTo: string;
  showCustomers: boolean;
};

export type defaultDateFilterWithAccountsType = {
  dateFrom: string;
  dateTo: string;
  accounts: number[] | Accounts[];
  includeMyData: boolean;
};

// temp until sp reports are able to filter type by account permissions. Once not temp, combine into accountType function below.
export const isBsi = (accountId: number) => environmentConfig.bsiAccountIds?.includes(accountId) ?? false;

export type DefaultDateFilterParams = {
  accountFilterShouldShow?: boolean;
  includeDistinct?: boolean;
  includeVideoType?: boolean;
  includePriority?: boolean;
  includeStatus?: boolean;
  includePacks?: boolean;
  includeAddons?: boolean;
  includeFlows?: boolean;
  initialDateRange?: PerformanceFilter;
} & (
  | {
      includeAccountsDropdown?: never;
      accountId?: never;
    }
  | {
      includeAccountsDropdown: boolean | undefined;
      accountId: number;
    }
);

export const defaultDateFilter = ({
  accountFilterShouldShow,
  initialDateRange = dateRange30(),
  includeAccountsDropdown,
  includeDistinct,
  includeVideoType,
  includePriority,
  includeStatus,
  includePacks,
  includeAddons,
  includeFlows,
}: DefaultDateFilterParams = {}) => {
  return {
    ...initialDateRange,
    ...(accountFilterShouldShow && { showCustomers: false }),
    ...(includeAccountsDropdown && { accounts: ["All Customer Accounts"] as any }),
    ...(includeAccountsDropdown && { includeMyData: true }),
    ...(includeDistinct && { isDistinct: false }),
    ...(includePriority && { priority: [] }),
    ...(includeStatus && { status: [] }),
    ...(includeVideoType && { videoType: "All" as const }),
    ...(includePacks && { packStatusId: 2 }),
    ...(includePacks && { packTypeId: 0 }),
    ...(includePacks && { packId: [] }),
    ...(includeAddons && { addOnId: [], isActive: null }),
    ...(includeFlows && { flowId: 0 }),
  };
};

export const dateRangeAllTime = (): PerformanceFilter => {
  const to = moment().startOf("day").toISOString();
  const from = moment("1970-01-01").startOf("day").toISOString();
  return longFormDateFilter(from, to);
};

export type QueryFilter = QueryFunctionContext<
  [_: string, filter: PerformanceWithAccountFilter, accountId?: number],
  unknown
>;

export type QueryFilterFlow = QueryFunctionContext<[_: string, filter: BasePerformanceRequestFilterParams], unknown>;

export type QueryFilterWithAccount = QueryFunctionContext<
  [_name: string, filter: PerformanceWithAccountFilter],
  unknown
>;

export const getFormattedTimeStringFromPeriod = (d: Date | string, period: ChartPeriod, dates: Date[]): string => {
  let dateToFormat = typeof d === "string" ? moment(d, SERVER_DATE_ONLY_FORMAT).local(true) : moment(d).local(true);
  switch (period) {
    case CHART_PERIOD.WEEK:
      let startMoment = moment(dates[0]);
      let startDate = dateToFormat.isBefore(startMoment, "day") ? startMoment : dateToFormat;
      // Test one week later
      let final = moment(dates.at(-1));
      let endDate = dateToFormat.clone().add(1, "week").subtract(1, "day");
      if (endDate.isAfter(final)) {
        endDate = final;
      }
      if (startDate.isSame(endDate, "day")) {
        return startDate.format("M/D");
      }

      return `${startDate.format("M/D")} - ${endDate.format("M/D")}`;
    case CHART_PERIOD.MONTH:
    case CHART_PERIOD.DAY:
    default:
      return dateToFormat.format("MMM DD").replace(",", "");
  }
};

export const largestToSmallest = (a: DataPoint, b: DataPoint) => b.value - a.value;
export const periodFormat = (period: ChartPeriod) => (period === CHART_PERIOD.MONTH ? "M/YY" : "M/D");

type User = { FirstName: string; LastName: string };
export function isUserDeleted(user: User): boolean;
export function isUserDeleted(fullName: string): boolean;
export function isUserDeleted(firstName: string | null, lastName: string | null): boolean;
export function isUserDeleted(firstNameOrFullName: string | User | null, lastName?: string | null): boolean {
  if (!firstNameOrFullName) {
    return false;
  }
  if (typeof firstNameOrFullName === "string") {
    if (!lastName) {
      return firstNameOrFullName.toLowerCase() === "removed user";
    }
    return `${firstNameOrFullName} ${lastName}`.toLowerCase() === "removed user";
  }
  // User type
  return isUserDeleted(firstNameOrFullName.FirstName, firstNameOrFullName.LastName);
}

export function formatUserName(fullName: string | null | undefined): string;
export function formatUserName(firstName: string | null | undefined, lastName: string | null | undefined): string;
export function formatUserName(
  nameOrFirstName: string | null | undefined,
  lastName?: string | null | undefined,
): string {
  const fallbackDisplay = "-";

  if (arguments.length === 1) {
    const fullName = nameOrFirstName;
    if (fullName && fullName.trim() !== "") {
      return fullName.trim();
    }
    return fallbackDisplay;
  }

  const fName = nameOrFirstName && nameOrFirstName.trim() !== "" ? nameOrFirstName.trim() : null;
  const lName = lastName && lastName.trim() !== "" ? lastName.trim() : null;

  if (!fName || !lName) {
    return fallbackDisplay;
  }

  return `${fName} ${lName}`;
}

export const queryHasData = (result: UseQueryResult<unknown[]>) => {
  return result.isSuccess && result.data.length > 0;
};
export const allCustomerAccounts = { text: "All Customer Accounts", value: "All Customer Accounts" };

export const fetchAccounts = async (setListOfAccounts: any) => {
  const top = 10000;
  let skip = 0;
  let listOfAccounts: Accounts[] = [];
  let foundAllAccounts = false;
  while (foundAllAccounts === false) {
    const { items } = await accountsDataService.getAccounts(skip, top, "name", SortingDirection.Ascending);
    if (items.flat().length === 0) {
      foundAllAccounts = true;
    } else {
      const tempListOfAccounts = items.flat().map((account) => {
        return { value: account.id, text: account.name };
      });
      listOfAccounts = [...listOfAccounts, ...tempListOfAccounts];
      if (items.flat().length === top) {
        skip += top;
      } else {
        foundAllAccounts = true;
      }
    }
  }

  listOfAccounts.unshift(allCustomerAccounts);
  setListOfAccounts(listOfAccounts.flat());
};

export const fallbackToZero = (value: number | undefined | null) => value ?? 0;

export const getPriorityCell = (priority: PriorityLevels) => {
  switch (priority) {
    case PriorityLevels.recommended:
      return (
        <div className={"priorityDescription"}>
          <FontAwesomeIcon icon={faSquareArrowDown} />
          <div>Recommended</div>
        </div>
      );
    case PriorityLevels.important:
      return (
        <div className={"priorityDescription"}>
          <FontAwesomeIcon icon={faSquareArrowUp} />
          <div>Important</div>
        </div>
      );
    case PriorityLevels.required:
      return (
        <div className={"priorityDescription"}>
          <FontAwesomeIcon icon={faSquareExclamation} />
          <div>Required</div>
        </div>
      );
    default:
      return <span>None</span>;
  }
};

export const hasSingleAccountFilter = (filter: PerformanceWithAccountFilter) => {
  // If there is only one account selected, it is not 'All Customer Accounts', and includeMyData is false.
  const isSingleCustomerAccountSelected =
    filter.accounts?.length === 1 && typeof filter.accounts?.[0] === "number" && filter.includeMyData === false;

  // If no accounts are selected, and includeMyData is true.
  const isOnlyOwnAccountSelected = filter.accounts?.length === 0 && filter.includeMyData === true;

  return isSingleCustomerAccountSelected || isOnlyOwnAccountSelected;
};

export const normalizeFlowFilter = (flowId: number | undefined) => {
  if (flowId === 0) {
    return undefined;
  }
  return flowId;
};

/*
Normalize the status array for reporting. If the array contains 3 ("Incomplete"), removes all 3s, adds 0 ("Not Started") and 1 ("Started"), and deduplicates the result. Otherwise, returns the original array unchanged. The backend does not support "Incomplete" status, so this is a workaround to ensure that it can be handled correctly.
*/
export const normalizeStatusFilter = (status: number[]) => {
  const notStartedStatus = 0;
  const startedStatus = 1;
  const incompleteStatus = 3;

  if (status?.includes(incompleteStatus)) {
    let filteredStatus = status.filter((s) => s !== incompleteStatus);
    filteredStatus = [...filteredStatus, notStartedStatus, startedStatus];
    filteredStatus = Array.from(new Set(filteredStatus));
    return filteredStatus;
  }
  return status;
};

/*
Normalize the pack status filter for reporting. This is due to our dropdown component seeing 0 as falsey. If the value is 3 ("Active"), converts it to 0 for backend compatibility. Otherwise, returns the original value unchanged. The backend expects "Active" to be 0, so this is a workaround to ensure that the UI enum value (3) can be correctly mapped to the backend value (0).
*/
export enum PackStatusFilter {
  Active = 3,
  Expired = 1,
  All = 2,
}

export const normalizePackStatusFilter = (packStatusId: PackStatusFilter) => {
  const activeStatusUI = PackStatusFilter.Active;
  const activeStatusBackend = 0;

  if (packStatusId === activeStatusUI) {
    return activeStatusBackend;
  }
  return packStatusId;
};

export const mappedPriorityLevels = [
  {
    key: "None",
    text: <span>None</span>,
    value: 0,
  },
  {
    key: "Recommended",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faSquareArrowDown} color={"#288bed"} />
        <span>Recommended</span>
      </div>
    ),
    value: 1,
  },
  {
    key: "Important",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faSquareArrowUp} color={"#1ed061"} />
        <span>Important</span>
      </div>
    ),
    value: 2,
  },
  {
    key: "Required",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faSquareExclamation} color={"#d01e1e"} />
        <span>Required</span>
      </div>
    ),
    value: 3,
  },
];

export const mappedStatusLevels = [
  {
    key: "Not Started",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faCircle} color={"#9c989e"} />
        <span>Not Started</span>
      </div>
    ),
    value: 0,
  },
  {
    key: "Started",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faClockNine} color={"#FFBF48"} />
        <span>Started</span>
      </div>
    ),
    value: 1,
  },
  {
    key: "Completed",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faCircleCheck} color={"#1ED061"} />
        <span>Completed</span>
      </div>
    ),
    value: 2,
  },
  {
    key: "Incomplete",
    text: (
      <div className={styles.status}>
        <FontAwesomeIcon icon={faCircleMinus} color={"#686569"} />
        <span>Incomplete (Not Started and Started)</span>
      </div>
    ),
    value: 3,
  },
];

export const customDropdownSearch = (list: DropdownItemProps[], searchString: string): DropdownItemProps[] =>
  list.filter((item: DropdownItemProps) => item.key.toLowerCase().includes(searchString.toLowerCase()));

export const convertInteractionDisplayName = (type: UserAssetTypes) => {
  if (type === "Event Registered") return "Event Registrations";
  if (type === "Event Attended") return "Events Attended";
  if (type === "Links Impressioned") return "Link Impressions";
  return type;
};

export const getRateString = (rate: number | undefined) => {
  if (rate === undefined) {
    return "-";
  }

  return `${roundToTwoDigits(rate * 100)}%`;
};

export const isDateFilterValid = (dateFilter: PerformanceWithAccountFilter | null) => {
  return dateFilter ?? null;
};
